// Pagina pubblica aperta dal link nell'email: /#/reimposta-password/:token
// All'apertura controlla solo il link (nessuna modifica): la password cambia solo quando il candidato conferma.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TextField, Button, Typography, IconButton, InputAdornment } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import PasswordResetLayout, { usePasswordResetStyles, LinkLogin } from './PasswordResetLayout';
import { verificaLink, confermaPassword, MIN_PASSWORD_LENGTH } from './passwordResetApi';

const MESSAGGI_ERRORE = {
    invalid_password: `La password deve avere almeno ${MIN_PASSWORD_LENGTH} caratteri.`,
    update_failed: 'Non è stato possibile salvare la password. Riprova tra qualche minuto.',
};

const ReimpostaPasswordPage = () => {
    const classes = usePasswordResetStyles();
    const { token } = useParams();
    const [stato, setStato] = useState('verifica'); // verifica | form | invio | completato | non_valido | errore
    const [password, setPassword] = useState('');
    const [conferma, setConferma] = useState('');
    const [mostraPassword, setMostraPassword] = useState(false);
    const [errore, setErrore] = useState('');
    // I gestori di password non compilano i campi in sola lettura: si sbloccano al primo focus dell'utente
    const [campiSbloccati, setCampiSbloccati] = useState(false);

    useEffect(() => {
        verificaLink(token)
            .then(valido => setStato(valido ? 'form' : 'non_valido'))
            .catch(() => setStato('errore'));
    }, [token]);

    const handleSubmit = async e => {
        e.preventDefault();
        setErrore('');

        if (password.length < MIN_PASSWORD_LENGTH) {
            setErrore(MESSAGGI_ERRORE.invalid_password);
            return;
        }
        if (password !== conferma) {
            setErrore('Le due password non coincidono.');
            return;
        }

        setStato('invio');
        try {
            const esito = await confermaPassword(token, password);
            if (esito.status === 'ok') {
                setStato('completato');
            } else if (esito.reason === 'invalid_token') {
                setStato('non_valido');
            } else {
                setErrore(MESSAGGI_ERRORE[esito.reason] || MESSAGGI_ERRORE.update_failed);
                setStato('form');
            }
        } catch (error) {
            setErrore(MESSAGGI_ERRORE.update_failed);
            setStato('form');
        }
    };

    const antiAutofill = {
        readOnly: !campiSbloccati,
        onFocus: () => setCampiSbloccati(true),
        'data-lpignore': 'true',
        'data-1p-ignore': 'true',
        'data-bwignore': 'true',
        'data-form-type': 'other',
    };

    const visibilita = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton onClick={() => setMostraPassword(!mostraPassword)}>
                    {mostraPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
            </InputAdornment>
        ),
    };

    return (
        <PasswordResetLayout title="IMPOSTA LA PASSWORD">
            {stato === 'verifica' && (
                <Typography variant="body1" className={classes.text}>Verifica del link in corso...</Typography>
            )}

            {stato === 'errore' && (
                <Typography variant="body1" color="error" className={classes.text}>
                    Non è stato possibile verificare il link. Riprova tra qualche minuto.
                </Typography>
            )}

            {stato === 'non_valido' && (
                <>
                    <Typography variant="body1" className={classes.text}>
                        Questo link non è più valido: è scaduto oppure è già stato usato.
                    </Typography>
                    <Link to="/password-dimenticata" className={classes.link}>Richiedi un nuovo link</Link>
                </>
            )}

            {stato === 'completato' && (
                <Typography variant="body1" className={classes.text}>
                    La password è stata impostata. Ora puoi accedere al quiz.
                </Typography>
            )}

            {(stato === 'form' || stato === 'invio') && (
                <form className={classes.form} onSubmit={handleSubmit}>
                    <Typography variant="body1" className={classes.text}>
                        Scegli la nuova password (almeno {MIN_PASSWORD_LENGTH} caratteri).
                    </Typography>
                    <TextField
                        label="Nuova password"
                        type={mostraPassword ? 'text' : 'password'}
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        InputProps={visibilita}
                        autoComplete="new-password"
                        inputProps={antiAutofill}
                    />
                    <TextField
                        label="Ripeti la nuova password"
                        type={mostraPassword ? 'text' : 'password'}
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        value={conferma}
                        onChange={e => setConferma(e.target.value)}
                        InputProps={visibilita}
                        autoComplete="new-password"
                        inputProps={antiAutofill}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        className={classes.submit}
                        disabled={stato === 'invio'}
                    >
                        Imposta password
                    </Button>
                    {errore && (
                        <Typography variant="body2" color="error" className={classes.text}>{errore}</Typography>
                    )}
                </form>
            )}

            <LinkLogin className={classes.link}>Vai alla pagina di accesso</LinkLogin>
        </PasswordResetLayout>
    );
};

export default ReimpostaPasswordPage;
